import { createContext, useContext } from 'react';

export type Language = 'en' | 'fr';

export const translations = {
  en: {
    common: {
      appName: 'Clyra',
      loading: 'Loading...',
      error: 'An error occurred',
      success: 'Success',
      cancel: 'Cancel',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      create: 'Create',
      search: 'Search',
      filter: 'Filter',
      sort: 'Sort',
      actions: 'Actions',
      status: 'Status',
      priority: 'Priority',
      dueDate: 'Due Date',
      project: 'Project',
      description: 'Description',
      noData: 'No data available',
    },
    auth: {
      signIn: 'Sign In',
      signUp: 'Sign Up',
      signOut: 'Sign Out',
      forgotPassword: 'Forgot Password?',
      resetPassword: 'Reset Password',
      email: 'Email',
      password: 'Password',
      confirmPassword: 'Confirm Password',
      name: 'Name',
      rememberMe: 'Remember Me',
      dontHaveAccount: "Don't have an account?",
      alreadyHaveAccount: 'Already have an account?',
    },
    landing: {
      hero: {
        title: 'Manage Tasks with',
        subtitle: 'Efficiency',
        description: 'Stay organized, meet deadlines, and achieve your goals with our powerful task management solution.',
        cta: 'Start for Free',
      },
      features: {
        title: 'Everything you need to stay productive',
        subtitle: 'Powerful features to help you manage tasks, track progress, and achieve your goals.',
        reminders: {
          title: 'Smart Reminders',
          description: 'Never miss a deadline with intelligent reminders and notifications for your important tasks.',
        },
        tracking: {
          title: 'Progress Tracking',
          description: 'Monitor your productivity with detailed analytics and progress tracking features.',
        },
        notifications: {
          title: 'Smart Notifications',
          description: 'Stay updated with real-time notifications about task updates and deadlines.',
        },
        security: {
          title: 'Secure & Private',
          description: 'Your data is protected with enterprise-grade security and encryption.',
        },
        quickActions: {
          title: 'Quick Actions',
          description: 'Streamline your workflow with intuitive shortcuts and quick actions.',
        },
        organization: {
          title: 'Task Organization',
          description: 'Keep your tasks organized with projects, priorities, and custom categories.',
        },
      },
      interface: {
        title: 'Beautiful and intuitive interface',
        subtitle: 'A clean, modern design that helps you focus on what matters most.',
      },
      cta: {
        title: 'Ready to boost your productivity?',
        subtitle: 'Join thousands of users who are already managing their tasks more efficiently.',
        primary: 'Get Started for Free',
        secondary: 'Already have an account? Sign in',
      },
    },
    tasks: {
      title: 'Tasks',
      addTask: 'Add Task',
      editTask: 'Edit Task',
      deleteTask: 'Delete Task',
      taskTitle: 'Task Title',
      status: {
        notStarted: 'Not Started',
        inProgress: 'In Progress',
        done: 'Done',
      },
      priority: {
        low: 'Low',
        medium: 'Medium',
        high: 'High',
      },
    },
    projects: {
      title: 'Projects',
      addProject: 'Add Project',
      editProject: 'Edit Project',
      deleteProject: 'Delete Project',
      projectName: 'Project Name',
      progress: 'Progress',
    },
    settings: {
      title: 'Settings',
      profile: 'Profile',
      preferences: 'Preferences',
      notifications: 'Notifications',
      language: 'Language',
      theme: 'Theme',
      security: 'Security',
    },
  },
  fr: {
    common: {
      appName: 'Clyra',
      loading: 'Chargement...',
      error: 'Une erreur est survenue',
      success: 'Succès',
      cancel: 'Annuler',
      save: 'Enregistrer',
      delete: 'Supprimer',
      edit: 'Modifier',
      create: 'Créer',
      search: 'Rechercher',
      filter: 'Filtrer',
      sort: 'Trier',
      actions: 'Actions',
      status: 'Statut',
      priority: 'Priorité',
      dueDate: "Date d'échéance",
      project: 'Projet',
      description: 'Description',
      noData: 'Aucune donnée disponible',
    },
    auth: {
      signIn: 'Se connecter',
      signUp: "S'inscrire",
      signOut: 'Se déconnecter',
      forgotPassword: 'Mot de passe oublié ?',
      resetPassword: 'Réinitialiser le mot de passe',
      email: 'Email',
      password: 'Mot de passe',
      confirmPassword: 'Confirmer le mot de passe',
      name: 'Nom',
      rememberMe: 'Se souvenir de moi',
      dontHaveAccount: "Vous n'avez pas de compte ?",
      alreadyHaveAccount: 'Vous avez déjà un compte ?',
    },
    landing: {
      hero: {
        title: 'Gérez vos tâches avec',
        subtitle: 'Efficacité',
        description: 'Restez organisé, respectez les délais et atteignez vos objectifs avec notre puissante solution de gestion des tâches.',
        cta: 'Commencer gratuitement',
      },
      features: {
        title: 'Tout ce dont vous avez besoin pour rester productif',
        subtitle: 'Des fonctionnalités puissantes pour vous aider à gérer les tâches, suivre les progrès et atteindre vos objectifs.',
        reminders: {
          title: 'Rappels intelligents',
          description: 'Ne manquez jamais une échéance grâce aux rappels intelligents et aux notifications pour vos tâches importantes.',
        },
        tracking: {
          title: 'Suivi des progrès',
          description: 'Surveillez votre productivité avec des analyses détaillées et des fonctionnalités de suivi des progrès.',
        },
        notifications: {
          title: 'Notifications intelligentes',
          description: 'Restez informé avec des notifications en temps réel sur les mises à jour et les échéances des tâches.',
        },
        security: {
          title: 'Sécurisé et privé',
          description: 'Vos données sont protégées avec une sécurité et un chiffrement de niveau entreprise.',
        },
        quickActions: {
          title: 'Actions rapides',
          description: 'Optimisez votre flux de travail avec des raccourcis intuitifs et des actions rapides.',
        },
        organization: {
          title: 'Organisation des tâches',
          description: 'Gardez vos tâches organisées avec des projets, des priorités et des catégories personnalisées.',
        },
      },
      interface: {
        title: 'Interface belle et intuitive',
        subtitle: 'Un design épuré et moderne qui vous aide à vous concentrer sur l\'essentiel.',
      },
      cta: {
        title: 'Prêt à booster votre productivité ?',
        subtitle: 'Rejoignez des milliers d\'utilisateurs qui gèrent déjà leurs tâches plus efficacement.',
        primary: 'Commencer gratuitement',
        secondary: 'Vous avez déjà un compte ? Connectez-vous',
      },
    },
    tasks: {
      title: 'Tâches',
      addTask: 'Ajouter une tâche',
      editTask: 'Modifier la tâche',
      deleteTask: 'Supprimer la tâche',
      taskTitle: 'Titre de la tâche',
      status: {
        notStarted: 'Non commencée',
        inProgress: 'En cours',
        done: 'Terminée',
      },
      priority: {
        low: 'Faible',
        medium: 'Moyenne',
        high: 'Haute',
      },
    },
    projects: {
      title: 'Projets',
      addProject: 'Ajouter un projet',
      editProject: 'Modifier le projet',
      deleteProject: 'Supprimer le projet',
      projectName: 'Nom du projet',
      progress: 'Progression',
    },
    settings: {
      title: 'Paramètres',
      profile: 'Profil',
      preferences: 'Préférences',
      notifications: 'Notifications',
      language: 'Langue',
      theme: 'Thème',
      security: 'Sécurité',
    },
  },
};

export type TranslationKey = keyof typeof translations.en;

interface I18nContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

export const I18nContext = createContext<I18nContextType | undefined>(undefined);

export const useTranslation = () => {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useTranslation must be used within an I18nProvider');
  }
  return context;
};